import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import type { Chord, MusicalKey, Mode, ScaleDegree, ChordQuality } from '@/types';
import type { PhraseBoundary, KeySegment } from '@/types/progression';
import type { NecklaceSettings, VoicePart } from '@/types/necklace';
import type { SuspensionConnector, SuspensionConnectorInput } from '@/types/suspension-connector';
// GRE-114: Unified types
import type {
  Sonority,
  SonorityInput,
  RepertoirePeriod,
} from '@/types/sonority';
import type { UnifiedSuspension } from '@/types/suspension-connector';
import {
  chordToSonority,
  sonorityToChord,
} from '@/types/sonority';
import {
  connectorToUnified,
  unifiedToConnector,
} from '@/types/suspension-connector';
import { CANVAS_CONFIG, DEFAULT_CHORD_SIZE } from '@/utils/constants';
import { DEFAULT_NECKLACE_SETTINGS } from '@/types/necklace';
import { DEFAULT_SUSPENSION_DURATION } from '@/types/suspension-connector';

interface CanvasState {
  // Chords
  chords: Chord[];

  // Phrase boundaries for visual grouping
  phrases: PhraseBoundary[];

  // Key segments from windowed key detection (Phase 2)
  keySegments: KeySegment[];

  // Chord annotations (chordId -> note)
  annotations: Record<string, string>;

  // Suspension connectors (GRE-113)
  suspensionConnectors: SuspensionConnector[];
  selectedConnectorId: string | null;
  isDraggingSuspension: boolean;

  // GRE-114: Unified types (coexist with legacy during migration)
  sonorities: Sonority[];
  unifiedSuspensions: UnifiedSuspension[];
  repertoirePeriod: RepertoirePeriod;

  // Selection
  selectedChordIds: string[];

  // Current key/mode
  currentKey: MusicalKey;
  currentMode: Mode;

  // Tempo
  tempo: number;

  // View settings
  zoom: number;
  showConnectionLines: boolean;

  // Playback
  isPlaying: boolean;
  playheadPosition: number;

  // Audio
  masterVolume: number;
  audioReady: boolean;

  // Necklace visualization
  necklaceSettings: NecklaceSettings;

  // Voice lines layer
  showVoiceLines: boolean;

  // Actions - Chords
  addChord: (chord: Partial<Chord> & { scaleDegree: ScaleDegree; position: { x: number; y: number } }) => Chord;
  removeChord: (id: string) => void;
  removeChords: (ids: string[]) => void;
  updateChord: (id: string, updates: Partial<Chord>) => void;
  updateChordPosition: (id: string, x: number, y: number) => void;
  clearChords: () => void;

  // Actions - Selection
  selectChord: (id: string) => void;
  selectChords: (ids: string[]) => void;
  deselectChord: (id: string) => void;
  toggleChordSelection: (id: string) => void;
  selectAll: () => void;
  selectRange: (fromId: string, toId: string) => void;
  clearSelection: () => void;

  // Actions - View
  setZoom: (zoom: number) => void;
  setCurrentKey: (key: MusicalKey) => void;
  setCurrentMode: (mode: Mode) => void;
  setTempo: (tempo: number) => void;
  toggleConnectionLines: () => void;

  // Actions - Load analyzed progression
  loadAnalyzedProgression: (options: {
    chords: Chord[];
    phrases?: PhraseBoundary[];
    keySegments?: KeySegment[];
    key: MusicalKey;
    mode: Mode;
    tempo: number;
  }) => void;

  // Actions - Clear phrases
  clearPhrases: () => void;

  // Actions - Annotations
  setAnnotation: (chordId: string, note: string) => void;
  removeAnnotation: (chordId: string) => void;
  clearAnnotations: () => void;

  // Actions - Playback
  setIsPlaying: (isPlaying: boolean) => void;
  setPlayheadPosition: (position: number) => void;

  // Actions - Audio
  setMasterVolume: (volume: number) => void;
  setAudioReady: (ready: boolean) => void;

  // Actions - Necklace
  setNecklaceVisible: (visible: boolean) => void;
  toggleVoiceNecklace: (voice: VoicePart) => void;
  setVoiceNecklaceColor: (voice: VoicePart, color: string) => void;
  setVoiceNecklaceOpacity: (voice: VoicePart, opacity: number) => void;
  resetNecklaceSettings: () => void;

  // Actions - Voice Lines
  setShowVoiceLines: (show: boolean) => void;
  toggleVoiceLines: () => void;

  // Actions - Suspension Connectors (GRE-113)
  addSuspensionConnector: (input: SuspensionConnectorInput) => string;
  removeSuspensionConnector: (id: string) => void;
  updateSuspensionConnector: (id: string, updates: Partial<SuspensionConnector>) => void;
  selectConnector: (id: string | null) => void;
  setDraggingSuspension: (isDragging: boolean) => void;
  clearSuspensionConnectors: () => void;

  // Actions - GRE-114: Unified Types
  setRepertoirePeriod: (period: RepertoirePeriod) => void;
  addSonority: (input: SonorityInput) => Sonority;
  updateSonority: (id: string, updates: Partial<Sonority>) => void;
  removeSonority: (id: string) => void;
  clearSonorities: () => void;
  addUnifiedSuspension: (suspension: Omit<UnifiedSuspension, 'id'>) => string;
  updateUnifiedSuspension: (id: string, updates: Partial<UnifiedSuspension>) => void;
  removeUnifiedSuspension: (id: string) => void;
  clearUnifiedSuspensions: () => void;
  // Sync utilities (migrate legacy to unified or vice versa)
  syncLegacyToUnified: () => void;
  syncUnifiedToLegacy: () => void;
}

// Helper to determine chord quality from scale degree and mode
function getDefaultQuality(scaleDegree: ScaleDegree, mode: Mode): ChordQuality {
  if (mode === 'major') {
    switch (scaleDegree) {
      case 1: case 4: case 5: return 'major';
      case 2: case 3: case 6: return 'minor';
      case 7: return 'diminished';
      default: return 'major';
    }
  } else {
    switch (scaleDegree) {
      case 1: case 4: return 'minor';
      case 3: case 6: case 7: return 'major';
      case 2: return 'diminished';
      case 5: return 'minor'; // Natural minor; could be major for harmonic minor
      default: return 'minor';
    }
  }
}

// Snap x position to nearest beat
function snapToBeat(x: number, zoom: number): number {
  const beatWidth = CANVAS_CONFIG.GRID_BEAT_WIDTH * zoom;
  return Math.round(x / beatWidth) * beatWidth;
}

export const useCanvasStore = create<CanvasState>((set, get) => ({
  // Initial state
  chords: [],
  phrases: [],
  keySegments: [],
  annotations: {},
  suspensionConnectors: [],
  selectedConnectorId: null,
  isDraggingSuspension: false,
  // GRE-114: Unified state
  sonorities: [],
  unifiedSuspensions: [],
  repertoirePeriod: 'contemporary' as RepertoirePeriod,
  selectedChordIds: [],
  currentKey: 'C',
  currentMode: 'major',
  tempo: 120,
  zoom: 1.0,
  showConnectionLines: true,
  isPlaying: false,
  playheadPosition: 0,
  masterVolume: 0.7,
  audioReady: false,
  necklaceSettings: DEFAULT_NECKLACE_SETTINGS,
  showVoiceLines: false,

  // Chord actions
  addChord: (chordInput) => {
    const { currentKey, currentMode, zoom } = get();
    const snappedX = snapToBeat(chordInput.position.x, zoom);
    const startBeat = Math.round(snappedX / (CANVAS_CONFIG.GRID_BEAT_WIDTH * zoom));

    const newChord: Chord = {
      id: uuidv4(),
      scaleDegree: chordInput.scaleDegree,
      quality: chordInput.quality || getDefaultQuality(chordInput.scaleDegree, currentMode),
      extensions: chordInput.extensions || {},
      key: currentKey,
      mode: currentMode,
      isChromatic: chordInput.isChromatic || false,
      voices: chordInput.voices || { soprano: 'C4', alto: 'G3', tenor: 'E3', bass: 'C3' },
      startBeat,
      duration: chordInput.duration || 4,
      position: { x: snappedX, y: chordInput.position.y },
      size: chordInput.size || DEFAULT_CHORD_SIZE,
      selected: false,
      playing: false,
      source: chordInput.source || 'user',
      createdAt: new Date().toISOString(),
    };

    set((state) => ({ chords: [...state.chords, newChord] }));

    return newChord;
  },

  removeChord: (id) => {
    set((state) => {
      const newAnnotations = { ...state.annotations };
      delete newAnnotations[id];
      return {
        chords: state.chords.filter((c) => c.id !== id),
        selectedChordIds: state.selectedChordIds.filter((cid) => cid !== id),
        annotations: newAnnotations,
      };
    });
  },

  removeChords: (ids) => {
    set((state) => {
      const newAnnotations = { ...state.annotations };
      ids.forEach((id) => delete newAnnotations[id]);
      return {
        chords: state.chords.filter((c) => !ids.includes(c.id)),
        selectedChordIds: state.selectedChordIds.filter((cid) => !ids.includes(cid)),
        annotations: newAnnotations,
      };
    });
  },

  updateChord: (id, updates) => {
    set((state) => ({
      chords: state.chords.map((c) =>
        c.id === id ? { ...c, ...updates } : c
      ),
    }));
  },

  updateChordPosition: (id, x, y) => {
    const { zoom } = get();
    const snappedX = snapToBeat(x, zoom);
    const startBeat = Math.round(snappedX / (CANVAS_CONFIG.GRID_BEAT_WIDTH * zoom));

    set((state) => ({
      chords: state.chords.map((c) =>
        c.id === id ? { ...c, position: { x: snappedX, y }, startBeat } : c
      ),
    }));
  },

  clearChords: () => {
    set({ chords: [], phrases: [], selectedChordIds: [], annotations: {} });
  },

  // Selection actions
  selectChord: (id) => {
    set({ selectedChordIds: [id] });
  },

  selectChords: (ids) => {
    set({ selectedChordIds: ids });
  },

  deselectChord: (id) => {
    set((state) => ({
      selectedChordIds: state.selectedChordIds.filter((cid) => cid !== id),
    }));
  },

  toggleChordSelection: (id) => {
    set((state) => {
      if (state.selectedChordIds.includes(id)) {
        return { selectedChordIds: state.selectedChordIds.filter((cid) => cid !== id) };
      }
      return { selectedChordIds: [...state.selectedChordIds, id] };
    });
  },

  selectAll: () => {
    set((state) => ({
      selectedChordIds: state.chords.map((c) => c.id),
    }));
  },

  selectRange: (fromId, toId) => {
    const { chords } = get();
    const sortedChords = [...chords].sort((a, b) => a.startBeat - b.startBeat);
    const fromIndex = sortedChords.findIndex((c) => c.id === fromId);
    const toIndex = sortedChords.findIndex((c) => c.id === toId);

    if (fromIndex === -1 || toIndex === -1) return;

    const start = Math.min(fromIndex, toIndex);
    const end = Math.max(fromIndex, toIndex);
    const rangeIds = sortedChords.slice(start, end + 1).map((c) => c.id);

    set({ selectedChordIds: rangeIds });
  },

  clearSelection: () => {
    set({ selectedChordIds: [] });
  },

  // View actions
  setZoom: (zoom) => {
    set({ zoom: Math.max(CANVAS_CONFIG.MIN_ZOOM, Math.min(CANVAS_CONFIG.MAX_ZOOM, zoom)) });
  },

  setCurrentKey: (key) => {
    set({ currentKey: key });
  },

  setCurrentMode: (mode) => {
    set({ currentMode: mode });
  },

  setTempo: (tempo) => {
    set({ tempo: Math.max(20, Math.min(300, tempo)) });
  },

  toggleConnectionLines: () => {
    set((state) => ({ showConnectionLines: !state.showConnectionLines }));
  },

  loadAnalyzedProgression: ({ chords, phrases, keySegments, key, mode, tempo }) => {
    set({
      chords,
      phrases: phrases || [],
      keySegments: keySegments || [],
      currentKey: key,
      currentMode: mode,
      tempo: Math.max(20, Math.min(300, Math.round(tempo))),
      selectedChordIds: [],
    });
  },

  clearPhrases: () => {
    set({ phrases: [] });
  },

  // Annotation actions
  setAnnotation: (chordId, note) => {
    set((state) => ({
      annotations: { ...state.annotations, [chordId]: note },
    }));
  },

  removeAnnotation: (chordId) => {
    set((state) => {
      const newAnnotations = { ...state.annotations };
      delete newAnnotations[chordId];
      return { annotations: newAnnotations };
    });
  },

  clearAnnotations: () => {
    set({ annotations: {} });
  },

  // Playback actions
  setIsPlaying: (isPlaying) => {
    set({ isPlaying });
  },

  setPlayheadPosition: (position) => {
    set({ playheadPosition: position });
  },

  // Audio actions
  setMasterVolume: (volume) => {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    set({ masterVolume: clampedVolume });
  },

  setAudioReady: (ready) => {
    set({ audioReady: ready });
  },

  // Necklace actions
  setNecklaceVisible: (visible) =>
    set((state) => ({
      necklaceSettings: { ...state.necklaceSettings, visible },
    })),

  toggleVoiceNecklace: (voice) =>
    set((state) => ({
      necklaceSettings: {
        ...state.necklaceSettings,
        voices: {
          ...state.necklaceSettings.voices,
          [voice]: {
            ...state.necklaceSettings.voices[voice],
            enabled: !state.necklaceSettings.voices[voice].enabled,
          },
        },
      },
    })),

  setVoiceNecklaceColor: (voice, color) =>
    set((state) => ({
      necklaceSettings: {
        ...state.necklaceSettings,
        voices: {
          ...state.necklaceSettings.voices,
          [voice]: {
            ...state.necklaceSettings.voices[voice],
            color,
          },
        },
      },
    })),

  setVoiceNecklaceOpacity: (voice, opacity) =>
    set((state) => ({
      necklaceSettings: {
        ...state.necklaceSettings,
        voices: {
          ...state.necklaceSettings.voices,
          [voice]: {
            ...state.necklaceSettings.voices[voice],
            opacity: Math.max(0, Math.min(1, opacity)),
          },
        },
      },
    })),

  resetNecklaceSettings: () =>
    set({ necklaceSettings: DEFAULT_NECKLACE_SETTINGS }),

  // Voice Lines actions
  setShowVoiceLines: (show) =>
    set({ showVoiceLines: show }),

  toggleVoiceLines: () =>
    set((state) => ({ showVoiceLines: !state.showVoiceLines })),

  // Suspension Connector actions (GRE-113)
  addSuspensionConnector: (input) => {
    const id = uuidv4();
    const connector: SuspensionConnector = {
      ...input,
      id,
      suspensionDuration: input.suspensionDuration ?? DEFAULT_SUSPENSION_DURATION,
      selected: false,
      playing: false,
    };
    set((state) => ({
      suspensionConnectors: [...state.suspensionConnectors, connector],
    }));
    return id;
  },

  removeSuspensionConnector: (id) =>
    set((state) => ({
      suspensionConnectors: state.suspensionConnectors.filter((c) => c.id !== id),
      selectedConnectorId: state.selectedConnectorId === id ? null : state.selectedConnectorId,
    })),

  updateSuspensionConnector: (id, updates) =>
    set((state) => ({
      suspensionConnectors: state.suspensionConnectors.map((c) =>
        c.id === id ? { ...c, ...updates } : c
      ),
    })),

  selectConnector: (id) =>
    set({ selectedConnectorId: id }),

  setDraggingSuspension: (isDragging) =>
    set({ isDraggingSuspension: isDragging }),

  clearSuspensionConnectors: () =>
    set({ suspensionConnectors: [], selectedConnectorId: null }),

  // GRE-114: Unified Type Actions
  setRepertoirePeriod: (period) =>
    set({ repertoirePeriod: period }),

  addSonority: (input) => {
    const { currentKey, currentMode, zoom } = get();
    const positionX = input.position?.x ?? 0;
    const positionY = input.position?.y ?? 100;
    const snappedX = snapToBeat(positionX, zoom);
    const startBeat = Math.round(snappedX / (CANVAS_CONFIG.GRID_BEAT_WIDTH * zoom));

    const newSonority: Sonority = {
      id: uuidv4(),
      origin: input.origin || 'user',
      scaleDegree: input.scaleDegree ?? 1,
      quality: input.quality || getDefaultQuality(input.scaleDegree ?? 1, currentMode),
      extensions: input.extensions || {},
      key: currentKey,
      mode: currentMode,
      isChromatic: input.isChromatic || false,
      chromaticType: input.chromaticType,
      voices: input.voices || { soprano: 'C4', alto: 'G3', tenor: 'E3', bass: 'C3' },
      startBeat,
      duration: input.duration || 4,
      position: { x: snappedX, y: positionY },
      size: input.size || DEFAULT_CHORD_SIZE,
      selected: false,
      playing: false,
      createdAt: new Date().toISOString(),
    };

    // Dual-write: also create legacy Chord
    const legacyChord = sonorityToChord(newSonority);

    set((state) => ({
      sonorities: [...state.sonorities, newSonority],
      chords: [...state.chords, legacyChord],
    }));

    return newSonority;
  },

  updateSonority: (id, updates) => {
    set((state) => {
      const updatedSonorities = state.sonorities.map((s) =>
        s.id === id ? { ...s, ...updates } : s
      );
      // Dual-write: also update legacy chord
      const updatedChords = state.chords.map((c) => {
        if (c.id === id) {
          const sonority = updatedSonorities.find((s) => s.id === id);
          return sonority ? sonorityToChord(sonority) : c;
        }
        return c;
      });
      return {
        sonorities: updatedSonorities,
        chords: updatedChords,
      };
    });
  },

  removeSonority: (id) => {
    set((state) => {
      const newAnnotations = { ...state.annotations };
      delete newAnnotations[id];
      return {
        sonorities: state.sonorities.filter((s) => s.id !== id),
        chords: state.chords.filter((c) => c.id !== id),
        selectedChordIds: state.selectedChordIds.filter((cid) => cid !== id),
        annotations: newAnnotations,
      };
    });
  },

  clearSonorities: () => {
    set({
      sonorities: [],
      chords: [],
      phrases: [],
      selectedChordIds: [],
      annotations: {},
    });
  },

  addUnifiedSuspension: (input) => {
    const id = uuidv4();
    const suspension: UnifiedSuspension = {
      ...input,
      id,
    };

    // Dual-write: also create legacy connector if possible
    const legacyConnector = unifiedToConnector(suspension);

    set((state) => ({
      unifiedSuspensions: [...state.unifiedSuspensions, suspension],
      suspensionConnectors: legacyConnector
        ? [...state.suspensionConnectors, legacyConnector]
        : state.suspensionConnectors,
    }));

    return id;
  },

  updateUnifiedSuspension: (id, updates) => {
    set((state) => {
      const updatedSuspensions = state.unifiedSuspensions.map((s) =>
        s.id === id ? { ...s, ...updates } : s
      );
      // Dual-write: also update legacy connector
      const updatedConnectors = state.suspensionConnectors.map((c) => {
        if (c.id === id) {
          const suspension = updatedSuspensions.find((s) => s.id === id);
          if (suspension) {
            const converted = unifiedToConnector(suspension);
            return converted || c;
          }
        }
        return c;
      });
      return {
        unifiedSuspensions: updatedSuspensions,
        suspensionConnectors: updatedConnectors,
      };
    });
  },

  removeUnifiedSuspension: (id) => {
    set((state) => ({
      unifiedSuspensions: state.unifiedSuspensions.filter((s) => s.id !== id),
      suspensionConnectors: state.suspensionConnectors.filter((c) => c.id !== id),
      selectedConnectorId: state.selectedConnectorId === id ? null : state.selectedConnectorId,
    }));
  },

  clearUnifiedSuspensions: () => {
    set({
      unifiedSuspensions: [],
      suspensionConnectors: [],
      selectedConnectorId: null,
    });
  },

  // Sync utilities for migration
  syncLegacyToUnified: () => {
    const { chords, suspensionConnectors } = get();
    const sonorities = chords.map(chordToSonority);
    const unifiedSuspensions = suspensionConnectors.map(connectorToUnified);
    set({ sonorities, unifiedSuspensions });
  },

  syncUnifiedToLegacy: () => {
    const { sonorities, unifiedSuspensions } = get();
    const chords = sonorities.map(sonorityToChord);
    const suspensionConnectors = unifiedSuspensions
      .map(unifiedToConnector)
      .filter((c): c is SuspensionConnector => c !== null);
    set({ chords, suspensionConnectors });
  },
}));
